import { ROAD_HALF_WIDTH, type RoadPath } from '@/modules/game-procedural/road-paths';
import { biomeToTerrainKey, type WorldTerrainContext } from '@/modules/game-procedural/procedural';
import { worldToScreen } from './coordinates';
import type { GameTextureManifest } from '@/game/assets/resolveGameAssetUrl';

export const ROAD_HALF_WIDTH_CELLS = ROAD_HALF_WIDTH.main;
type Point = { x: number; y: number };
export type ScreenBounds = { minX: number; maxX: number; minY: number; maxY: number };

const BIOME_FLOORS: Record<string, readonly [string, string, string, string, string]> = {
  'terrain.forest_floor.v1': ['terrain.forest_floor.v1', 'terrain.forest_floor.moss.v1', 'terrain.forest_floor.summer_grass.v1', 'terrain.forest_floor.stone.v1', 'terrain.forest_floor.undergrowth.v1'],
  'terrain.grass.v1': ['terrain.grass.v1', 'terrain.meadow_floor.dense_grass.v1', 'terrain.meadow_floor.clover.v1', 'terrain.meadow_floor.wildflowers.v1', 'terrain.meadow_floor.dry_grass.v1'],
  'terrain.swamp_floor.v1': ['terrain.swamp_floor.v1', 'terrain.swamp_floor.moss.v1', 'terrain.swamp_floor.silt.v1', 'terrain.swamp_floor.clay.v1', 'terrain.swamp_floor.groundcover.v1'],
  'terrain.canyon_floor.v1': ['terrain.canyon_floor.v1', 'terrain.canyon_floor.sand.v1', 'terrain.canyon_floor.redrock.v1', 'terrain.canyon_floor.shale.v1', 'terrain.canyon_floor.gravel.v1'],
  'terrain.ruins_floor.v1': ['terrain.ruins_floor.v1', 'terrain.ruins_floor.cracked.v1', 'terrain.ruins_floor.moss.v1', 'terrain.ruins_floor.ash.v1', 'terrain.ruins_floor.sand.v1'],
  'terrain.volcanic_floor.v1': ['terrain.volcanic_floor.v1', 'terrain.volcanic_floor.ash.v1', 'terrain.volcanic_floor.lava.v1', 'terrain.volcanic_floor.scoria.v1', 'terrain.volcanic_floor.iron.v1'],
  'terrain.astral_floor.v1': ['terrain.astral_floor.v1', 'terrain.astral_floor.dust.v1', 'terrain.astral_floor.mineral.v1', 'terrain.astral_floor.teal.v1', 'terrain.astral_floor.crater.v1'],
  'terrain.winter_floor.v1': ['terrain.winter_floor.v1', 'terrain.winter_floor.snow.v1', 'terrain.winter_floor.ice.v1', 'terrain.winter_floor.gravel.v1', 'terrain.winter_floor.pine_litter.v1'],
  'terrain.jungle_floor.v1': ['terrain.jungle_floor.v1', 'terrain.jungle_floor.moss.v1', 'terrain.jungle_floor.silt.v1', 'terrain.jungle_floor.groundcover.v1', 'terrain.jungle_floor.roots.v1'],
};
const BIOME_FLOOR_IDS: Record<string, string> = {
  'terrain.forest_floor.v1': 'forest_floor',
  'terrain.grass.v1': 'meadow_floor',
  'terrain.swamp_floor.v1': 'swamp_floor',
  'terrain.canyon_floor.v1': 'canyon_floor',
  'terrain.ruins_floor.v1': 'ruins_floor',
  'terrain.volcanic_floor.v1': 'volcanic_floor',
  'terrain.astral_floor.v1': 'astral_floor',
  'terrain.winter_floor.v1': 'winter_floor',
  'terrain.jungle_floor.v1': 'jungle_floor',
};
const BIOME_FLOOR_VARIANT_IDS: Record<string, readonly [string, string, string, string, string]> = {
  'terrain.forest_floor.v1': ['floor', 'moss', 'summer_grass', 'stone', 'undergrowth'],
  'terrain.grass.v1': ['floor', 'dense_grass', 'clover', 'wildflowers', 'dry_grass'],
  'terrain.swamp_floor.v1': ['floor', 'moss', 'silt', 'clay', 'groundcover'],
  'terrain.canyon_floor.v1': ['floor', 'sand', 'redrock', 'shale', 'gravel'],
  'terrain.ruins_floor.v1': ['floor', 'cracked', 'moss', 'ash', 'sand'],
  'terrain.volcanic_floor.v1': ['floor', 'ash', 'lava', 'scoria', 'iron'],
  'terrain.astral_floor.v1': ['floor', 'dust', 'mineral', 'teal', 'crater'],
  'terrain.winter_floor.v1': ['floor', 'snow', 'ice', 'gravel', 'pine_litter'],
  'terrain.jungle_floor.v1': ['floor', 'moss', 'silt', 'groundcover', 'roots'],
};
const BIOME_FLOOR_OFFSETS: Record<string, { x: number; y: number }> = Object.fromEntries(
  Object.keys(BIOME_FLOORS).map(key => {
    const seed = key === 'terrain.forest_floor.v1' ? 0 : [...key].reduce((sum, char) => (sum * 31 + char.charCodeAt(0)) | 0, 7);
    return [key, { x: (Math.abs(seed) % 89) * .71, y: (Math.abs(seed >>> 7) % 97) * .67 }];
  }),
);
const FLOOR_TYPES = [0, 1, 2, 3, 4] as const;
export type BiomeFloorType = typeof FLOOR_TYPES[number];
export type BiomeTileEdge = 'ne' | 'se' | 'sw' | 'nw';

function hashNoise(x: number, y: number): number {
  const n = Math.sin(x * 127.1 + y * 311.7 + 19.19) * 43758.5453;
  return (n - Math.floor(n)) * 2 - 1;
}

function smoothNoise(x: number, y: number, scale: number): number {
  const gx = x / scale, gy = y / scale, ix = Math.floor(gx), iy = Math.floor(gy);
  const sx = (gx - ix) ** 2 * (3 - 2 * (gx - ix));
  const sy = (gy - iy) ** 2 * (3 - 2 * (gy - iy));
  const a = hashNoise(ix, iy) * (1 - sx) + hashNoise(ix + 1, iy) * sx;
  const b = hashNoise(ix, iy + 1) * (1 - sx) + hashNoise(ix + 1, iy + 1) * sx;
  return a * (1 - sy) + b * sy;
}

/** Pick a floor material from a warped low-frequency field to form broad organic patches. */
export function biomeFloorType(wx: number, wy: number, baseKey = 'terrain.forest_floor.v1'): BiomeFloorType {
  const { x: ox, y: oy } = BIOME_FLOOR_OFFSETS[baseKey] ?? { x: 0, y: 0 };
  const sx = wx + ox, sy = wy + oy;
  const x = sx + smoothNoise(sx, sy, 24) * 5.5;
  const y = sy + smoothNoise(sx + 91, sy - 37, 24) * 5.5;
  const field = smoothNoise(x, y, 14) * .76 + smoothNoise(sx - 23, sy + 17, 6) * .24;
  return field < -.25 ? 0 : field < -.02 ? 1 : field < .16 ? 2 : field < .37 ? 3 : 4;
}

export function biomeFloorTextureKey(baseKey: string, wx: number, wy: number): string | null {
  const keys = BIOME_FLOORS[baseKey];
  return keys ? keys[biomeFloorType(wx, wy, baseKey)] : null;
}

export function biomeFloorTextureKeys(baseKey: string): readonly string[] | null {
  return BIOME_FLOORS[baseKey] ?? null;
}

/** Transition overlays are authored only from the lower numbered patch into the higher. */
export function biomeFloorTransitionTextureKey(from: BiomeFloorType, to: BiomeFloorType,
  edge: BiomeTileEdge, baseKey = 'terrain.forest_floor.v1'): string | null {
  if (from >= to) return null;
  if (!BIOME_FLOORS[baseKey]) return null;
  const biome = BIOME_FLOOR_IDS[baseKey];
  const names = BIOME_FLOOR_VARIANT_IDS[baseKey];
  if (!biome || !names) return null;
  return `terrain.${biome}.transition.${names[from]}-to-${names[to]}.${edge}.v1`;
}

/** Convert a screen-space neighbor edge to the atlas edge before mirroring the overlay. */
export function biomeTileLocalEdge(edge: BiomeTileEdge, mirror: { x: boolean; y: boolean }): BiomeTileEdge {
  let result = edge;
  if (mirror.x) result = ({ ne: 'nw', se: 'sw', sw: 'se', nw: 'ne' } as const)[result];
  if (mirror.y) result = ({ ne: 'se', se: 'ne', sw: 'nw', nw: 'sw' } as const)[result];
  return result;
}

/** Stable low-frequency mirrors further break repeats within each material patch. */
export function biomeFloorMirror(wx: number, wy: number): { x: boolean; y: boolean } {
  // Long broad swaths, softened by a smaller-scale warp so the boundaries aren't rectangular.
  const nx = wx + smoothNoise(wx, wy, 31) * 7;
  const ny = wy + smoothNoise(wx + 73, wy - 41, 31) * 7;
  const patch = smoothNoise(nx, ny, 18) * .82 + smoothNoise(wx - 17, wy + 29, 8) * .18;
  return { x: patch > .02, y: patch < -.28 };
}

/** Presentation only: gameplay still receives its original proceduralTerrain value. */
export function coherentTerrain(wx: number, wy: number, context: WorldTerrainContext | null,
  legacyTerrain: string, texturedRoads: boolean): string {
  if (legacyTerrain === 'plaza') return 'plaza';
  if (!texturedRoads && legacyTerrain === 'road') return 'road';
  const towns = context?.towns ?? [];
  const hasBiomeTown = towns.some(town => town.biome !== 'road');
  let nearest = towns[0];
  let distance = Infinity;
  for (const town of towns) {
    if (hasBiomeTown && town.biome === 'road') continue;
    const d = Math.hypot(wx - town.cx, wy - town.cy);
    if (d < distance) { distance = d; nearest = town; }
  }
  if (nearest) return biomeToTerrainKey(nearest.biome);
  return context?.wildernessBiome ? biomeToTerrainKey(context.wildernessBiome) : 'grass';
}

/** Preserve server centerlines exactly; rounding joins must never reroute the road. */
type PathCache = { network: WorldTerrainContext['roadNetwork']; styles: WorldTerrainContext['roadPaths']; route: WorldTerrainContext['activeRoute']; towns: WorldTerrainContext['towns']; centers: WorldTerrainContext['networkCenters']; styled: readonly RoadPath[]; paths: readonly (readonly Point[])[] };
const roadPathCache = new WeakMap<WorldTerrainContext, PathCache>();
function cachedRoadPaths(context: WorldTerrainContext): PathCache {
  const cached=roadPathCache.get(context);
  if(cached&&cached.network===context.roadNetwork&&cached.styles===context.roadPaths&&cached.route===context.activeRoute&&cached.towns===context.towns&&cached.centers===context.networkCenters)return cached;
  const paths:RoadPath[] = context.roadPaths ? context.roadPaths.filter(path=>path.points.length>=2) : context.roadNetwork.filter(path=>path.length>=2).map(points=>({points,kind:'main'}));
  if(context.activeRoute&&context.activeRoute.length>=2)paths.push({points:context.activeRoute,kind:'main'});
  if(!paths.length&&context.networkCenters.length>=2)paths.push({points:[...context.networkCenters,context.networkCenters[0]!],kind:'main'});
  const styled=context.towns.some(t=>t.authored&&t.authoredBounds) ? paths.flatMap(path=>roadOutsideAuthoredTowns(path.points,context).map(points=>({points,kind:path.kind}))) : paths;
  const result:PathCache={network:context.roadNetwork,styles:context.roadPaths,route:context.activeRoute,towns:context.towns,centers:context.networkCenters,styled,paths:styled.map(path=>path.points)};
  roadPathCache.set(context,result);return result;
}
export function presentationStyledRoadPaths(context: WorldTerrainContext | null): readonly RoadPath[] { return context ? cachedRoadPaths(context).styled : []; }
export function presentationRoadPaths(context: WorldTerrainContext | null): readonly (readonly Point[])[] { return context ? cachedRoadPaths(context).paths : []; }

/** Remove legacy road artwork inside authored towns; exact DTO streets own that ground. */
export function roadOutsideAuthoredTowns(path: readonly Point[],context: WorldTerrainContext): Point[][] {
  const polygons = context.towns.flatMap(t => {
    if (!t.authored) return [];
    if (t.authoredPerimeter?.length) return [t.authoredPerimeter];
    const r=t.authoredBounds;
    return r ? [[{x:r.minX,y:r.minY},{x:r.maxX,y:r.minY},{x:r.maxX,y:r.maxY},{x:r.minX,y:r.maxY}]] : [];
  });
  if (!polygons.length) return [[...path]];
  const inside = (point:Point,polygon:readonly Point[]) => {
    let result=false;
    for(let i=0,j=polygon.length-1;i<polygon.length;j=i++) {
      const a=polygon[i]!,b=polygon[j]!;
      if((a.y>point.y)!==(b.y>point.y) && point.x<(b.x-a.x)*(point.y-a.y)/(b.y-a.y)+a.x)result=!result;
    }
    return result;
  };
  const boundedPolygons = polygons.map(polygon => ({ polygon,
    minX: Math.min(...polygon.map(p=>p.x)), maxX: Math.max(...polygon.map(p=>p.x)),
    minY: Math.min(...polygon.map(p=>p.y)), maxY: Math.max(...polygon.map(p=>p.y)),
  }));
  const output:Point[][]=[];
  const append = (a:Point,b:Point) => {
    const previous=output[output.length-1],last=previous?.[previous.length-1];
    if(last && last.x===a.x && last.y===a.y) previous!.push(b);
    else output.push([a,b]);
  };
  for(let i=1;i<path.length;i++) {
    const a=path[i-1]!,b=path[i]!,dx=b.x-a.x,dy=b.y-a.y;
    if(!dx&&!dy)continue;
    const nearby = boundedPolygons.filter(p=>Math.max(a.x,b.x)>=p.minX&&Math.min(a.x,b.x)<=p.maxX&&Math.max(a.y,b.y)>=p.minY&&Math.min(a.y,b.y)<=p.maxY);
    if(!nearby.length){append(a,b);continue;}
    const cuts=[0,1];
    for(const {polygon} of nearby) for(let j=0;j<polygon.length;j++) {
      const c=polygon[j]!,d=polygon[(j+1)%polygon.length]!,ex=d.x-c.x,ey=d.y-c.y;
      const denominator=dx*ey-dy*ex;
      if(Math.abs(denominator)<1e-10)continue;
      const t=((c.x-a.x)*ey-(c.y-a.y)*ex)/denominator;
      const u=((c.x-a.x)*dy-(c.y-a.y)*dx)/denominator;
      if(t>0&&t<1&&u>=0&&u<=1)cuts.push(t);
    }
    cuts.sort((x,y)=>x-y);
    for(let j=1;j<cuts.length;j++) {
      const lo=cuts[j-1]!,hi=cuts[j]!,mid=(lo+hi)/2;
      if(hi-lo<1e-10||nearby.some(p=>inside({x:a.x+dx*mid,y:a.y+dy*mid},p.polygon)))continue;
      append(lo===0?a:{x:a.x+dx*lo,y:a.y+dy*lo},hi===1?b:{x:a.x+dx*hi,y:a.y+dy*hi});
    }
  }
  return output;
}

/** Visible contiguous segments, including crossings whose two endpoints are offscreen. */
export function visibleRoadSegments(points: readonly Point[], bounds: ScreenBounds, padding = 96): Point[][] {
  const chunks: Point[][] = [];
  let current: Point[] | null = null;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1]!, b = points[i]!;
    const sa = worldToScreen(a.x, a.y), sb = worldToScreen(b.x, b.y);
    const visible = [sa.x, sa.y, sb.x, sb.y].every(Number.isFinite)
      && Math.min(sa.x, sb.x) <= bounds.maxX + padding && Math.max(sa.x, sb.x) >= bounds.minX - padding
      && Math.min(sa.y, sb.y) <= bounds.maxY + padding && Math.max(sa.y, sb.y) >= bounds.minY - padding;
    if (!visible) { current = null; continue; }
    if (!current) { current = [a]; chunks.push(current); }
    current.push(b);
  }
  return chunks;
}

/** Isometric ribbon in world units, with capped miters at sharp turns. */
export function roadRibbonPolygon(points: readonly Point[], halfWidth: number = ROAD_HALF_WIDTH_CELLS): number[] {
  const clean = points.filter((point, i) => !i || point.x !== points[i - 1]!.x || point.y !== points[i - 1]!.y);
  if (clean.length < 2 || !Number.isFinite(halfWidth) || halfWidth <= 0) return [];
  const left: Point[] = [], right: Point[] = [];
  const normal = (a: Point, b: Point) => {
    const length = Math.hypot(b.x - a.x, b.y - a.y);
    return { x: -(b.y - a.y) / length, y: (b.x - a.x) / length };
  };
  for (let i = 0; i < clean.length; i++) {
    const point = clean[i]!;
    const before = normal(clean[Math.max(0, i - 1)]!, clean[Math.max(1, i)]!);
    const after = normal(clean[Math.min(i, clean.length - 2)]!, clean[Math.min(i + 1, clean.length - 1)]!);
    const length = Math.hypot(before.x + after.x, before.y + after.y);
    const nx = length > 1e-6 ? (before.x + after.x) / length : after.x;
    const ny = length > 1e-6 ? (before.y + after.y) / length : after.y;
    const dot = Math.abs(nx * after.x + ny * after.y);
    const offset = Math.min(halfWidth / Math.max(dot, 0.01), halfWidth * 1.5);
    left.push(worldToScreen(point.x + nx * offset, point.y + ny * offset));
    right.push(worldToScreen(point.x - nx * offset, point.y - ny * offset));
  }
  return [...left, ...right.reverse()].flatMap(point => [point.x, point.y]);
}

/** A visual family override never changes procedural object kind, location or blocking rules. */
const BIOME_GROUND_OBJECTS = new Set(['tree', 'bush', 'stump', 'fallen_log', 'meadow_wildflowers', 'rock', 'cactus',
  'volcanic_basalt', 'astral_crystals', 'marsh_reeds', 'swamp_berry_bush', 'ruin_rubble', 'snow_drift', 'leaves',
  'barrel', 'crate', 'cart', 'lantern', 'bench', 'hay_bale', 'wagon_wheel', 'planter']);
export function biomeObjectTextureKey(defaultKey: string | null, objectType: string, variant: number,
  wx: number, wy: number, context: WorldTerrainContext | null,
  mappings: NonNullable<GameTextureManifest['worldPresentation']>['biomeObjects'],
  hasTexture: (key: string) => boolean): string | null {
  if (!mappings || !BIOME_GROUND_OBJECTS.has(objectType)) return defaultKey;
  const terrain = coherentTerrain(wx, wy, context, 'grass', true);
  const family = mappings[terrain]?.[objectType];
  if (!Array.isArray(family) || !family.length) return defaultKey;
  const index = Number.isInteger(variant) && variant >= 0 && variant < family.length
    ? variant : Math.min(family.length - 1, Math.max(0, Math.floor((Number.isFinite(variant) ? variant : 0) * family.length)));
  const key = family[index];
  return typeof key === 'string' && hasTexture(key) ? key : defaultKey;
}
